import React, { useCallback, useEffect, useState } from "react";
import aiDevelopmentService from "../../services/aiDevelopmentService";

export default function AiJiraStorySyncPanel({ storyId, originSystem, t }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await aiDevelopmentService.storyJiraStatus(storyId));
    } catch {
      setStatus(null);
    }
  }, [storyId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!status || !status.jiraUrl) return null;

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setBusy(false);
    }
  };

  const resolve = (conflictId, resolution) =>
    run(() => aiDevelopmentService.resolveJiraConflict(conflictId, { resolution }));

  return (
    <div className="ai-jira-sync-panel">
      <h3>{t("aiDevelopment.jiraSync.storyTitle")}</h3>
      {error && <div className="ai-studio-alert">{error}</div>}
      <div className="ai-jira-sync-status">
        <span>{t("aiDevelopment.jiraSync.storyOrigin")}: {t(`aiDevelopment.jiraSync.origin${originSystem === "JIRA" ? "Jira" : "Idax"}`)}</span>
        <a href={status.jiraUrl} target="_blank" rel="noreferrer">{status.jiraIssueKey}</a>
        <span className="ai-status">{t(`aiDevelopment.jiraSync.linkStatus${status.linkStatus}`)}</span>
        {status.lastSyncedAt && (
          <span>{t("aiDevelopment.jiraSync.lastSynced")}: {new Date(status.lastSyncedAt).toLocaleString()}</span>
        )}
        {status.lastError && <span className="ai-status ai-status-error">{status.lastError}</span>}
        {status.filteredReason && <span className="ai-status ai-status-error">{status.filteredReason}</span>}
      </div>
      <div className="ai-actions">
        <button type="button" className="btn btn-default" disabled={busy} onClick={() => run(() => aiDevelopmentService.retryJiraSync(storyId))}>
          {t("aiDevelopment.jiraSync.retry")}
        </button>
        {status.linkStatus === "FILTERED" ? (
          <button type="button" className="btn btn-default" disabled={busy} onClick={() => run(() => aiDevelopmentService.includeInJiraSync(storyId))}>
            {t("aiDevelopment.jiraSync.include")}
          </button>
        ) : (
          <button type="button" className="btn btn-default" disabled={busy} onClick={() => run(() => aiDevelopmentService.excludeFromJiraSync(storyId))}>
            {t("aiDevelopment.jiraSync.exclude")}
          </button>
        )}
      </div>
      {status.pendingConflicts?.length > 0 && (
        <div className="ai-jira-sync-conflicts">
          <h4>{t("aiDevelopment.jiraSync.conflictsTitle")}</h4>
          {status.pendingConflicts.map((c) => (
            <div className="ai-jira-sync-conflict" key={c.id}>
              <strong>{c.fieldName}</strong>
              <div className="ai-jira-sync-conflict-values">
                <span>{t("aiDevelopment.jiraSync.idaxValue")}: {c.idaxValue}</span>
                <span>{t("aiDevelopment.jiraSync.jiraValue")}: {c.jiraValue}</span>
              </div>
              <div className="ai-actions">
                <button type="button" className="btn btn-default" onClick={() => resolve(c.id, "RESOLVED_IDAX")}>
                  {t("aiDevelopment.jiraSync.resolveIdax")}
                </button>
                <button type="button" className="btn btn-default" onClick={() => resolve(c.id, "RESOLVED_EXTERNAL")}>
                  {t("aiDevelopment.jiraSync.resolveJira")}
                </button>
                <button type="button" className="btn btn-default" onClick={() => resolve(c.id, "RESOLVED_MANUAL")}>
                  {t("aiDevelopment.jiraSync.resolveManual")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
